import React, { useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import ChangePasswordModal from '../modals/ChangePasswordModal';
import SettingsModal from '../modals/SettingsModal';
import LogoutModal from '../modals/LogoutModal';
import Button from '../common/Button';

const Sidebar = ({ activePage, setActivePage, sidebarOpen, setSidebarOpen }) => {
  const { logout, user, isAdmin } = useAuth();
  const navigate = useNavigate();
  const [changePasswordOpen, setChangePasswordOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [logoutOpen, setLogoutOpen] = useState(false);

  const menuItems = [
    { id: 'dashboard', icon: 'fas fa-chart-line', label: 'Dashboard', page: 'dashboard' },
    { id: 'daily-business', icon: 'fas fa-business-time', label: 'Daily Business', page: 'daily-business' },
    { id: 'stock-check', icon: 'fas fa-boxes', label: 'Stock Check', page: 'stock-check' },
    { id: 'liter-statement', icon: 'fas fa-file-alt', label: 'Liter Statement', page: 'liter-statement' },
    // User Management & Roles - Admin only
    ...(isAdmin ? [
      { id: 'users', icon: 'fas fa-users', label: 'User Management', page: 'users' },
      { id: 'roles', icon: 'fas fa-user-tag', label: 'Roles & Permissions', page: 'roles' }
    ] : []),
    { id: 'reports', icon: 'fas fa-chart-bar', label: 'Reports', page: 'reports' }
  ];

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const handleMenuClick = (item) => {
    if (item.page) {
      setActivePage(item.page);
      setSidebarOpen(false);
    }
  };

  return (
    <>
      {/* Sidebar - a single responsive tree: slides in on mobile via transform,
          permanently visible on desktop via md:translate-x-0 */}
      <aside
        className={`fixed left-0 top-0 h-full w-72 md:w-64 bg-white dark:bg-slate-800 shadow-xl flex flex-col gap-6 md:gap-4 p-6 md:p-4 pt-[max(1.5rem,env(safe-area-inset-top))] md:pt-[max(1rem,env(safe-area-inset-top))] pb-[max(1.5rem,env(safe-area-inset-bottom))] md:pb-[max(1rem,env(safe-area-inset-bottom))] pl-[max(1.5rem,env(safe-area-inset-left))] md:pl-[max(1rem,env(safe-area-inset-left))] border-r border-slate-200/60 dark:border-slate-700 z-50 transform transition-transform duration-300 md:translate-x-0 ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        <div className="flex items-center justify-between gap-2 shrink-0">
          <div className="flex-1 text-xl md:text-2xl font-extrabold text-center py-4 gradient-text relative">
            <div className="flex items-center justify-center gap-2">
              <div className="w-8 h-8 md:w-10 md:h-10 bg-gradient-to-br from-primary to-accent rounded-xl flex items-center justify-center shadow-md shadow-primary/15">
                <i className="fas fa-chart-line text-white text-sm md:text-lg"></i>
              </div>
              <span className="font-bold">BizTracker</span>
            </div>
            <div className="hidden md:block absolute bottom-2 left-1/4 w-1/2 h-0.5 bg-gradient-to-r from-transparent via-primary/50 to-transparent rounded-full"></div>
          </div>

        </div>

        <nav className="flex-1 overflow-y-auto" aria-label="Main navigation">
          <ul className="space-y-1.5">
            {menuItems.map((item) => {
              const isActive = item.page && activePage === item.page;
              return (
                <li key={item.id}>
                  <button
                    onClick={() => handleMenuClick(item)}
                    aria-current={isActive ? 'page' : undefined}
                    className={`w-full flex items-center gap-2.5 px-4 py-3 rounded-xl transition-all duration-200 group relative ${
                      isActive
                        ? 'bg-gradient-to-r from-primary to-primary-dark text-white shadow-md shadow-primary/20'
                        : 'text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700/60 hover:text-slate-900 dark:hover:text-white'
                    }`}
                  >
                    <i className={`${item.icon} text-base ${isActive ? 'text-white' : 'text-primary/80 group-hover:scale-110'} transition-transform duration-200`} aria-hidden="true"></i>
                    <span className="font-semibold text-sm">{item.label}</span>
                    {isActive && (
                      <div className="hidden md:block absolute right-0 top-1/2 -translate-y-1/2 w-1.5 h-6 bg-white rounded-l-full"></div>
                    )}
                  </button>
                </li>
              );
            })}
          </ul>
        </nav>

        <div className="border-t border-slate-100 dark:border-slate-700 pt-4 shrink-0">
          <button
            onClick={() => setUserMenuOpen((prev) => !prev)}
            aria-expanded={userMenuOpen}
            className="w-full flex items-center justify-between px-3.5 py-3 rounded-xl bg-slate-50 dark:bg-slate-700/50 hover:bg-slate-100 dark:hover:bg-slate-700 border border-slate-100 dark:border-slate-700 transition-all duration-200"
          >
            <div className="flex items-center gap-3 min-w-0">
              <div className="w-9 h-9 md:w-10 md:h-10 rounded-full bg-gradient-to-br from-primary to-accent text-white flex items-center justify-center shadow shrink-0 animate-pulse-slow">
                <i className="fas fa-user text-sm md:text-base" aria-hidden="true"></i>
              </div>
              <div className="text-left min-w-0">
                <p className="text-sm font-bold text-slate-800 dark:text-slate-100 truncate">{user?.name || 'User'}</p>
                <p className="text-xs text-slate-400 dark:text-slate-500 font-semibold truncate">{isAdmin ? 'Administrator' : (user?.currentRole || 'Member')}</p>
              </div>
            </div>
            <i className={`fas fa-chevron-down text-xs text-slate-400 dark:text-slate-500 transition-transform duration-300 shrink-0 ${userMenuOpen ? 'rotate-180' : ''}`} aria-hidden="true"></i>
          </button>

          {userMenuOpen && (
            <div className="mt-4 space-y-3 animate-fade-in">
              <Button
                variant="outline"
                fullWidth
                icon="fa-cog"
                onClick={() => {
                  setSettingsOpen(true);
                  setSidebarOpen(false);
                }}
              >
                Settings
              </Button>
              <Button
                variant="outline"
                fullWidth
                icon="fa-key"
                onClick={() => {
                  setChangePasswordOpen(true);
                  setSidebarOpen(false);
                }}
              >
                Change Password
              </Button>
              <Button
                variant="danger"
                fullWidth
                icon="fa-sign-out-alt"
                onClick={() => {
                  setLogoutOpen(true);
                  setUserMenuOpen(false);
                }}
              >
                Logout
              </Button>
            </div>
          )}
        </div>
      </aside>

      <ChangePasswordModal
        isOpen={changePasswordOpen}
        onClose={() => setChangePasswordOpen(false)}
      />
      <SettingsModal
        isOpen={settingsOpen}
        onClose={() => setSettingsOpen(false)}
      />
      <LogoutModal
        isOpen={logoutOpen}
        onClose={() => setLogoutOpen(false)}
        onConfirm={handleLogout}
      />
    </>
  );
};

export default Sidebar;
